import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../i18n/LanguageStore';
// import the supported languages array/object directly
import { SUPPORTED_LANGUAGES } from '../i18n/supportedLanguages';
import { useCallback, useMemo, useEffect } from 'react';

/**
 * Custom hook for internationalization
 * Combines react-i18next with our Zustand language store
 */
export function useI18n() {
  // Get translation functions from react-i18next
  const { t, i18n } = useTranslation();

  // Get language state from Zustand store
  const { currentLanguage, isLoading, setLanguage } = useLanguageStore();

  // Ensure store reflects persisted i18next language on mount (prevents selector showing default)
  useEffect(() => {
    const resolved = (i18n.resolvedLanguage || i18n.language || '').trim();
    if (resolved && resolved !== currentLanguage) {
      // Don't call i18n.changeLanguage here (i18next is already the source of truth on boot)
      useLanguageStore.setState({ currentLanguage: resolved });
    }

    // Also normalize regional codes like "cs-CZ" -> "cs"
    const base = resolved.split('-')[0];
    if (base && base !== resolved && base !== currentLanguage) {
      useLanguageStore.setState({ currentLanguage: base });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create a wrapper for t function that handles namespaces more intelligently
  const tWithNamespace = useCallback(
    (key: string, options?: any) => {
      // If the key already contains a namespace (has a colon), use it directly
      if (key.includes(':')) {
        return t(key, options);
      }

      // If caller explicitly provided a namespace, respect it.
      if (options?.ns) {
        return t(key, options);
      }

      // Check if the key starts with specific prefixes and add appropriate namespace
      if (key.startsWith('nav.')) {
        return t(key, { ...options, ns: 'navigation' });
      }

      if (key.startsWith('settings.')) {
        return t(key, { ...options, ns: 'settings' });
      }

      if (key.startsWith('auth.')) {
        return t(key, { ...options, ns: 'auth' });
      }

      if (key.startsWith('booking.')) {
        return t(key, { ...options, ns: 'booking' });
      }

      // Default to common namespace
      return t(key, { ...options, ns: 'common' });
    },
    [t]
  );

  // Get list of supported languages - memoized to prevent unnecessary re-renders
  const supportedLanguages = useMemo(
    () =>
      SUPPORTED_LANGUAGES.map((lang: { code: string }) => ({
        code: lang.code,
        name: t(`language.${lang.code}`, { ns: 'common' }),
        nativeName: t(`language.${lang.code}_native`, { ns: 'common' }),
      })),
    [t]
  );

  return {
    // Translation function
    t: tWithNamespace,
    // Current language
    currentLanguage,
    // i18next instance
    i18n,
    // Loading state
    isLoading,
    // Function to change language
    setLanguage,
    // List of supported languages
    supportedLanguages,
  };
}